import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { faqApi } from "../../services/faq.api";
import { FaqDto, FaqListResponse } from "../../types/faq.definition";
import {
  createFaqFailure,
  createFaqRequest,
  createFaqSuccess,
  deleteFaqFailure,
  deleteFaqRequest,
  deleteFaqSuccess,
  fetchFaqsFailure,
  fetchFaqsRequest,
  fetchFaqsSuccess,
  updateFaqFailure,
  updateFaqRequest,
  updateFaqSuccess,
} from "../reducers/faqSlice";

function* fetchFaqsSaga(action: ReturnType<typeof fetchFaqsRequest>) {
  try {
    const { lang = "fr" } = action.payload;
    // GET /faqs returns the full tree (root FAQs with nested children).
    // We store the whole tree and navigate it locally — no per-level API calls needed.
    const result: FaqListResponse = yield call(faqApi.getFaqs, lang, 0, 1000);
    yield put(
      fetchFaqsSuccess({
        data: result.data,
        totalCount: result.data.length,
        lang,
      })
    );
  } catch (error) {
    yield put(
      fetchFaqsFailure(
        error instanceof Error ? error.message : "Failed to fetch FAQs"
      )
    );
  }
}

function* createFaqSaga(action: ReturnType<typeof createFaqRequest>) {
  try {
    const { data, onSuccess } = action.payload;
    const created: FaqDto = yield call(faqApi.createFaq, data);
    yield put(createFaqSuccess(created));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(
      createFaqFailure(
        error instanceof Error ? error.message : "Failed to create FAQ"
      )
    );
  }
}

function* updateFaqSaga(action: ReturnType<typeof updateFaqRequest>) {
  try {
    const { id, data, onSuccess } = action.payload;
    const updated: FaqDto = yield call(faqApi.updateFaq, id, data);
    yield put(updateFaqSuccess(updated));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(
      updateFaqFailure(
        error instanceof Error ? error.message : "Failed to update FAQ"
      )
    );
  }
}

function* deleteFaqSaga(action: ReturnType<typeof deleteFaqRequest>) {
  try {
    const { id, onSuccess } = action.payload;
    yield call(faqApi.deleteFaq, id);
    yield put(deleteFaqSuccess(id));
    if (onSuccess) onSuccess();
  } catch (error) {
    // The Axios interceptor in api.config.ts rejects with a plain { code, message, category }
    // object, so the error code is directly on the error, not nested under response.data.
    const code: string =
      error instanceof Object &&
      "code" in error &&
      typeof (error as Record<string, unknown>).code === "string"
        ? ((error as Record<string, unknown>).code as string)
        : "UNKNOWN";
    yield put(deleteFaqFailure(code));
    const { onError } = action.payload;
    if (onError) onError(code);
  }
}

export function* faqSaga() {
  yield takeLatest(fetchFaqsRequest, fetchFaqsSaga);
  yield takeEvery(createFaqRequest, createFaqSaga);
  yield takeEvery(updateFaqRequest, updateFaqSaga);
  yield takeEvery(deleteFaqRequest, deleteFaqSaga);
}
